import React, { useEffect, useState } from "react";
import { Archive, ArchiveRestore, ChevronDown, ChevronRight, Pencil, Plus } from "lucide-react";
import AddButton from "@/components/Core/AddButton/AddButton";
import AppAlertDialog from "@/components/Core/AppAlertDialog/AppAlertDialog";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { CompetenceItem } from "@/models/Competence";
import { toast } from "@/utils/toast";

/** Path to a node in the tree — also the PARENT path when adding a child. */
export interface CompetenceKey {
	branch?: string;
	service?: string;
	skill?: string;
}

type Level = "branch" | "service" | "skill";

const LEVEL_LABELS: Record<Level, string> = {
	branch: "bransch",
	service: "tjänst",
	skill: "kompetens",
};

interface Props {
	/** The UNFILTERED tree (archived included) — admins manage archived nodes here. */
	competences: CompetenceItem[];
	/** Disables all actions while a mutation is in flight. */
	isSaving?: boolean;
	onRename: (key: CompetenceKey, svName: string) => void;
	/** Archive (true) or restore (false) the node at `key`. */
	onArchive: (key: CompetenceKey, archived: boolean) => void;
	/** Add a child under `parentKey` (empty = new branch). Keys are generated server side. */
	onAdd: (parentKey: CompetenceKey, svName: string) => void;
	/**
	 * Permanently delete an archived node. Hidden — only offered while Shift is
	 * held. Rejects with error code COMPETENCE_IN_USE while consultants hold it
	 * or jobs reference it.
	 */
	onDelete?: (key: CompetenceKey) => Promise<unknown>;
}

type EditorDialog =
	| { mode: "add"; parentKey: CompetenceKey; childLevel: Level; parentName?: string }
	| { mode: "rename"; key: CompetenceKey; level: Level; currentName: string };

const iconButtonClasses =
	"flex h-7 w-7 items-center justify-center rounded-sm border border-transparent text-gray-400 transition-colors hover:border-gray-200 hover:bg-gray-50 hover:text-gray-700 disabled:pointer-events-none disabled:opacity-50";
const primaryButtonClasses =
	"rounded-md bg-brand px-3.5 py-1.5 text-sm font-medium text-brand-foreground transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50";
const secondaryButtonClasses =
	"rounded-md border border-gray-300 bg-white px-3.5 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-50";

/** True while the Shift key is held down (reset when the window loses focus). */
const useShiftHeld = () => {
	const [held, setHeld] = useState(false);

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => setHeld(e.shiftKey);
		const onBlur = () => setHeld(false);

		window.addEventListener("keydown", onKey);
		window.addEventListener("keyup", onKey);
		window.addEventListener("blur", onBlur);

		return () => {
			window.removeEventListener("keydown", onKey);
			window.removeEventListener("keyup", onKey);
			window.removeEventListener("blur", onBlur);
		};
	}, []);

	return held;
};

const ArchivedBadge = () => (
	<span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-500">
		Arkiverad
	</span>
);

/**
 * Admin editor for the organisation competence tree
 * (bransch → tjänst → kompetens).
 *
 * Rename, add and archive/restore: archived nodes stay in the tree so existing
 * jobs, prices and labels keep working, but are hidden from all pickers. New
 * keys are generated server side from the Swedish name.
 *
 * Holding Shift reveals a hidden "Ta bort" action next to the Arkiverad badge
 * that permanently deletes the node — blocked server side while any
 * consultant holds the competence or any job references it.
 */
const CompetenceTree: React.FC<Props> = ({
	competences,
	isSaving,
	onRename,
	onArchive,
	onAdd,
	onDelete,
}) => {
	const shiftHeld = useShiftHeld();
	const [expandedBranches, setExpandedBranches] = useState<Set<string>>(new Set());
	const [expandedServices, setExpandedServices] = useState<Set<string>>(new Set());
	const [dialog, setDialog] = useState<EditorDialog | null>(null);
	const [draftName, setDraftName] = useState("");
	const [toArchive, setToArchive] = useState<{
		key: CompetenceKey;
		level: Level;
		name: string;
	} | null>(null);

	const [toDelete, setToDelete] = useState<{
		key: CompetenceKey;
		level: Level;
		name: string;
	} | null>(null);

	const confirmDelete = async () => {
		if (!toDelete || !onDelete) return;

		const target = toDelete;

		try {
			await onDelete(target.key);
			toast.success(`${target.name} har tagits bort`);
		} catch (err) {
			// Mirrors TaxonomyCrudEditor: the code sits on `error` or on the error itself.
			const anyErr = err as any;
			const code = anyErr?.error?.code ?? anyErr?.code;

			if (code === "COMPETENCE_IN_USE") {
				toast.error(`${target.name} kan inte tas bort`, {
					description: `${
						LEVEL_LABELS[target.level][0].toUpperCase() + LEVEL_LABELS[target.level].slice(1)
					}en används av konsulter eller uppdrag. Den kan bara tas bort när ingen konsult har den och inget uppdrag använder den.`,
				});
			} else {
				toast.errorGeneric();
			}
		}

		setToDelete(null);
	};

	const archivedBadge = (key: CompetenceKey, level: Level, item: CompetenceItem) => (
		<>
			<ArchivedBadge />
			{shiftHeld && onDelete && (
				<button
					type="button"
					disabled={isSaving}
					onClick={() => setToDelete({ key, level, name: item.name.sv ?? "" })}
					className="shrink-0 rounded border-0 bg-transparent px-1 py-0.5 text-xs font-medium text-red-600 hover:underline disabled:pointer-events-none disabled:opacity-50"
				>
					Ta bort
				</button>
			)}
		</>
	);

	const toggle = (set: Set<string>, key: string) => {
		const next = new Set(set);
		next.has(key) ? next.delete(key) : next.add(key);
		return next;
	};

	const openAdd = (parentKey: CompetenceKey, childLevel: Level, parentName?: string) => {
		setDraftName("");
		setDialog({ mode: "add", parentKey, childLevel, parentName });
	};

	const openRename = (key: CompetenceKey, level: Level, currentName: string) => {
		setDraftName(currentName);
		setDialog({ mode: "rename", key, level, currentName });
	};

	const submitDialog = () => {
		if (!dialog || !draftName.trim()) return;

		if (dialog.mode === "add") {
			onAdd(dialog.parentKey, draftName.trim());
		} else {
			onRename(dialog.key, draftName.trim());
		}

		setDialog(null);
	};

	const actionButtons = (
		key: CompetenceKey,
		level: Level,
		item: CompetenceItem,
		addChildLevel?: Level
	) => (
		<div className="ml-auto flex shrink-0 items-center gap-0.5">
			<button
				type="button"
				disabled={isSaving}
				onClick={() => openRename(key, level, item.name.sv ?? "")}
				aria-label="Byt namn"
				title="Byt namn"
				className={iconButtonClasses}
			>
				<Pencil size={14} />
			</button>
			{addChildLevel && !item.archived && (
				<button
					type="button"
					disabled={isSaving}
					onClick={() => openAdd(key, addChildLevel, item.name.sv)}
					aria-label={`Lägg till ${LEVEL_LABELS[addChildLevel]}`}
					title={`Lägg till ${LEVEL_LABELS[addChildLevel]}`}
					className={iconButtonClasses}
				>
					<Plus size={15} />
				</button>
			)}
			{item.archived ? (
				<button
					type="button"
					disabled={isSaving}
					onClick={() => onArchive(key, false)}
					aria-label="Återställ"
					title="Återställ"
					className={iconButtonClasses}
				>
					<ArchiveRestore size={14} />
				</button>
			) : (
				<button
					type="button"
					disabled={isSaving}
					onClick={() => setToArchive({ key, level, name: item.name.sv ?? "" })}
					aria-label="Arkivera"
					title="Arkivera"
					className={cn(
						iconButtonClasses,
						"hover:border-red-200 hover:bg-red-50 hover:text-red-600"
					)}
				>
					<Archive size={14} />
				</button>
			)}
		</div>
	);

	return (
		<div className="max-w-3xl space-y-4">
			{competences.length === 0 ? (
				<p className="text-sm text-gray-500">Inga branscher har skapats ännu.</p>
			) : (
				<div className="divide-y divide-gray-100 rounded-md border border-gray-200">
					{competences.map((branch) => {
						const branchExpanded = expandedBranches.has(branch.key);

						return (
							<div key={branch.key}>
								<div className="flex items-center gap-x-2 px-3 py-2.5">
									<button
										type="button"
										onClick={() => setExpandedBranches(toggle(expandedBranches, branch.key))}
										aria-label={branchExpanded ? "Fäll ihop" : "Fäll ut"}
										className="flex h-7 w-7 shrink-0 items-center justify-center rounded-sm border-0 bg-transparent text-gray-400 hover:text-gray-700"
									>
										{branchExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
									</button>
									<span
										className={cn(
											"truncate text-sm font-medium",
											branch.archived ? "text-gray-400" : "text-gray-800"
										)}
									>
										{branch.name.sv}
									</span>
									<span className="shrink-0 text-xs text-gray-400">
										({branch.items?.length ?? 0})
									</span>
									{branch.archived && archivedBadge({ branch: branch.key }, "branch", branch)}
									{actionButtons({ branch: branch.key }, "branch", branch, "service")}
								</div>

								{branchExpanded && (
									<div className="border-0 border-t border-solid border-gray-100 bg-gray-50/50 pb-1">
										{(branch.items ?? []).map((service) => {
											const serviceMapKey = `${branch.key}|${service.key}`;
											const serviceExpanded = expandedServices.has(serviceMapKey);
											const serviceArchived = service.archived || branch.archived;

											return (
												<div key={service.key}>
													<div className="flex items-center gap-x-2 py-1.5 pl-9 pr-3">
														{service.items?.length ? (
															<button
																type="button"
																onClick={() =>
																	setExpandedServices(toggle(expandedServices, serviceMapKey))
																}
																aria-label={serviceExpanded ? "Fäll ihop" : "Fäll ut"}
																className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm border-0 bg-transparent text-gray-400 hover:text-gray-700"
															>
																{serviceExpanded ? (
																	<ChevronDown size={14} />
																) : (
																	<ChevronRight size={14} />
																)}
															</button>
														) : (
															<span className="h-6 w-6 shrink-0" />
														)}
														<span
															className={cn(
																"truncate text-sm",
																serviceArchived ? "text-gray-400" : "text-gray-700"
															)}
														>
															{service.name.sv}
														</span>
														{!!service.items?.length && (
															<span className="shrink-0 text-xs text-gray-400">
																({service.items.length})
															</span>
														)}
														{service.archived &&
															archivedBadge(
																{ branch: branch.key, service: service.key },
																"service",
																service
															)}
														{actionButtons(
															{ branch: branch.key, service: service.key },
															"service",
															service,
															"skill"
														)}
													</div>

													{serviceExpanded &&
														(service.items ?? []).map((skill) => (
															<div
																key={skill.key}
																className="flex items-center gap-x-2 py-1 pl-[4.25rem] pr-3"
															>
																<span
																	className={cn(
																		"truncate text-sm",
																		skill.archived || serviceArchived
																			? "text-gray-400"
																			: "text-gray-600"
																	)}
																>
																	{skill.name.sv}
																</span>
																{skill.archived &&
																	archivedBadge(
																		{
																			branch: branch.key,
																			service: service.key,
																			skill: skill.key,
																		},
																		"skill",
																		skill
																	)}
																{actionButtons(
																	{
																		branch: branch.key,
																		service: service.key,
																		skill: skill.key,
																	},
																	"skill",
																	skill
																)}
															</div>
														))}
												</div>
											);
										})}
										{(branch.items ?? []).length === 0 && (
											<p className="py-2 pl-9 pr-3 text-sm text-gray-400">Inga tjänster ännu.</p>
										)}
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}

			<AddButton
				label="Lägg till bransch"
				disabled={isSaving}
				onClick={() => openAdd({}, "branch")}
			/>

			<Dialog open={!!dialog} onOpenChange={(open) => !open && setDialog(null)}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>
							{dialog?.mode === "add"
								? `Lägg till ${LEVEL_LABELS[dialog.childLevel]}`
								: dialog
								? `Byt namn på ${LEVEL_LABELS[dialog.level]}`
								: ""}
						</DialogTitle>
						{dialog?.mode === "add" && (
							<DialogDescription>
								{dialog.parentName ? `Läggs till under ${dialog.parentName}. ` : ""}
								{dialog.childLevel === "service" &&
									"Tänk på att nya tjänster behöver prisrader på kundernas avtal innan uppdrag kan skapas."}
							</DialogDescription>
						)}
					</DialogHeader>
					<Input
						autoFocus
						value={draftName}
						placeholder="Namn"
						onChange={(e) => setDraftName(e.target.value)}
						onKeyDown={(e) => e.key === "Enter" && submitDialog()}
					/>
					<DialogFooter>
						<button
							type="button"
							className={secondaryButtonClasses}
							onClick={() => setDialog(null)}
						>
							Avbryt
						</button>
						<button
							type="button"
							className={primaryButtonClasses}
							disabled={!draftName.trim() || isSaving}
							onClick={submitDialog}
						>
							{dialog?.mode === "add" ? "Lägg till" : "Spara"}
						</button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<AppAlertDialog
				open={!!toArchive}
				onOpenChange={(open) => !open && setToArchive(null)}
				skipTranslation
				title={toArchive ? `Arkivera ${toArchive.name}?` : ""}
				description={
					toArchive
						? `${
								toArchive.name
						  } döljs för nya uppdrag och i alla listor, men befintliga uppdrag och priser påverkas inte. Du kan återställa ${
								LEVEL_LABELS[toArchive.level]
						  }en när som helst.`
						: undefined
				}
				confirmLabel="Arkivera"
				cancelLabel="Avbryt"
				variant="danger"
				loading={isSaving}
				onConfirm={() => {
					if (toArchive) onArchive(toArchive.key, true);
					setToArchive(null);
				}}
			/>

			<AppAlertDialog
				open={!!toDelete}
				onOpenChange={(open) => !open && setToDelete(null)}
				skipTranslation
				title={toDelete ? `Ta bort ${toDelete.name}?` : ""}
				description={
					toDelete
						? `${toDelete.name}${
								toDelete.level === "skill" ? "" : " och allt under den"
						  } tas bort permanent och kan inte återställas. Det går bara om ingen konsult har ${
								LEVEL_LABELS[toDelete.level]
						  }en och inget uppdrag använder den.`
						: undefined
				}
				confirmLabel="Ta bort"
				cancelLabel="Avbryt"
				variant="danger"
				loading={isSaving}
				onConfirm={confirmDelete}
			/>
		</div>
	);
};

export default CompetenceTree;
